import { InteractionStatus } from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal
} from "@azure/msal-react";
import { loginWebdUser } from "api/services/LoginService";
import { WebDUser } from "api/services/models/WebDUser";
import { Login } from "components/Login";
import { PicklistContextProvider } from "components/picklists/PicklistContextProvider";
import { getAxiosErrorMessage } from "helpers/utils";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { loginRequest } from "./authConfig";
import { Spin } from "antd";
import { Route, Routes, useNavigate } from "react-router-dom";
import { AppRedirect } from "components/AppRedirect";
import DscribeGuide from "components/vscribe-guides/DscribeGuide";
import VscribeGuide from "components/vscribe-guides/VscribeGuide";
import { useOnlineStatus } from "useOnlineStatus";

type TUserContext = {
  loginUser?: () => void;
  logoutUser?: () => void;
  webDUser?: WebDUser | null;
  accountId?: string | null;
  updateAccountId?: (accountId: string) => void;
};

const UserContext = createContext<TUserContext>({});

export const AuthenticationProvider = ({ children }: { children: React.ReactNode }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [webDUser, setWebDUser] = useState<WebDUser | null>(null);
  const [accountId, setAccountId] = useState<string | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isOnline = useOnlineStatus();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && !webDUser) {
      setIsLoading(true);
      setError(null);
      const webDUser = localStorage.getItem("webDUser");
      if (webDUser) {
        setWebDUser(new WebDUser(JSON.parse(webDUser)));
        setIsLoading(false);
        return;
      }
      loginWebdUser()
        .then(setWebDUser)
        .catch(setError)
        .finally(() => setIsLoading(false));
    }
  }, [isAuthenticated, webDUser]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "globalLogout") {
        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [navigate]);


  const updateAccountId = useCallback((accountId: string) => {
    setAccountId(accountId);
  }, []);

  const loginUser = useCallback(() => instance.loginRedirect(loginRequest), [instance]);
  const logoutUser = useCallback(() => {
    const logoutRequest = {
      // account: instance?.getActiveAccount(),
      postLogoutRedirectUri: "/",
      logoutHint: instance?.getActiveAccount()?.idTokenClaims?.login_hint
    };
    setIsLoading(true);
    localStorage.setItem("globalLogout", Date.now().toString());
    setWebDUser(null);
    localStorage.removeItem("webDUser"); 
    instance
      .handleRedirectPromise()
      .then(() => instance.logoutRedirect(logoutRequest))
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [instance]);

  const contextValue = useMemo(
    () => ({
      loginUser,
      logoutUser,
      webDUser,
      accountId: accountId ?? webDUser?.user?.accountId ?? "",
      updateAccountId
    }),
    [webDUser, accountId, loginUser, logoutUser, updateAccountId]
  );

  if (inProgress !== InteractionStatus.None || isLoading) {
    return <Spin fullscreen />;
  }
  if (error) {
    return <div>{`Server Error: ${getAxiosErrorMessage(error)}`}</div>;
  }

  return (
    <div>
      <AuthenticatedTemplate>
        {webDUser ? (
          <>
            {isOnline ? (
              <UserContext.Provider value={contextValue}>
                <PicklistContextProvider>{children}</PicklistContextProvider>
              </UserContext.Provider>
            ) : (
              <Routes>
                <Route path="/guides/vscribeplus-d" element={<DscribeGuide />} />
                <Route path="/guides/vscribeplus-v" element={<VscribeGuide />} />
                <Route path="*" element={<p>Network error</p>} />
              </Routes>
            )}
          </>
        ) : (
          <div>
            <p>Inactive user, please contact customer support</p>
          </div>
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Routes>
          <Route path="/app-redirect" element={<AppRedirect />} />
          <Route path="*" element={<Login onLogin={loginUser} />} />
        </Routes>
      </UnauthenticatedTemplate>
    </div>
  );
};

export function useTDAuthentication() {
  const context = useContext(UserContext);
  if (context == null) {
    throw new Error("Invalid authentication context");
  }
  return context;
}
